import { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/App';
import { ScrollArea } from '@/components/ui/scroll-area';

const ChatBot = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const response = await api.get('/chat/history?limit=20');
      const history = response.data.flatMap(msg => [
        { role: 'user', content: msg.message },
        { role: 'assistant', content: msg.response }
      ]);
      setMessages(history);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/chat/message', { message: userMessage });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="chatbot-widget"
      className="fixed bottom-24 right-6 w-96 h-[500px] rounded-2xl shadow-2xl flex flex-col"
      style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
    >
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between" style={{borderColor: '#6FCFD3', backgroundColor: '#6FCFD3'}}>
        <h3 className="font-bold text-lg" style={{color: '#fff'}}>Mustang Assistant</h3>
        <button data-testid="close-chat-btn" onClick={onClose}>
          <X className="w-5 h-5" style={{color: '#fff'}} />
        </button>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4" style={{maxHeight: '380px'}}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p style={{color: '#372F2F', opacity: 0.7}}>Ask me anything about mustangs, adoption, or horse care!</p>
          </div>
        )}
        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              data-testid={`chat-message-${msg.role}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className="max-w-[80%] p-3 rounded-2xl"
                style={{
                  backgroundColor: msg.role === 'user' ? '#D66238' : '#6FCFD3',
                  color: '#fff'
                }}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="p-3 rounded-2xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 border-t" style={{borderColor: '#6FCFD3'}}>
        <div className="flex gap-2">
          <Input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about mustangs..."
            disabled={loading}
            className="flex-1"
          />
          <Button
            data-testid="send-message-btn"
            type="submit"
            disabled={loading || !input.trim()}
            size="icon"
            className="rounded-full"
            style={{backgroundColor: '#6FCFD3', color: '#fff'}}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChatBot;